// ============================================
// Digital Signs - Display Player Page
// Kiosk mode slideshow viewer for displays
// URL: /player
// ============================================

import React, { useState, useEffect, useCallback, useRef } from 'react';

const API_BASE = '/api/trpc';

// API helper
async function apiCall(procedure, input = {}, token = null) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['X-Display-Token'] = token;
  
  const res = await fetch(`${API_BASE}/${procedure}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error.message || 'API Error');
  return data.result?.data;
}

// Day mapping
const DAY_MAP = { 0: 'sun', 1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu', 5: 'fri', 6: 'sat' };

// Check if content should be shown based on schedule
function shouldShowContent(item) {
  if (!item.isScheduled) return true;
  
  const now = new Date();
  const currentDay = DAY_MAP[now.getDay()];
  const currentTime = now.getTime();
  
  // Check day of week
  if (item.scheduleDays) {
    const allowedDays = item.scheduleDays.toLowerCase().split(',');
    if (!allowedDays.includes(currentDay)) return false;
  }
  
  // Check date range
  if (item.startDateTime) {
    const startTime = new Date(item.startDateTime).getTime();
    if (currentTime < startTime) return false;
  }
  
  if (item.endDateTime) {
    const endTime = new Date(item.endDateTime).getTime();
    if (currentTime > endTime) return false;
  }
  
  return true;
}

// Get transform style for rotation/flip
function getTransformStyle(item) {
  const transforms = [];
  if (item.rotation && item.rotation !== '0') {
    transforms.push(`rotate(${item.rotation}deg)`);
  }
  if (item.flipHorizontal) {
    transforms.push('scaleX(-1)');
  }
  if (item.flipVertical) {
    transforms.push('scaleY(-1)');
  }
  return transforms.length > 0 ? transforms.join(' ') : 'none';
}

// Transition CSS
function getTransitionCSS(effect, duration = 500) {
  const baseTransition = `all ${duration}ms ease-in-out`;
  
  switch (effect) {
    case 'fade':
      return { transition: `opacity ${duration}ms ease-in-out` };
    case 'slide':
      return { transition: `transform ${duration}ms ease-in-out` };
    case 'dissolve':
      return { transition: `opacity ${duration}ms ease-in-out, filter ${duration}ms ease-in-out` };
    case 'checker':
      return { transition: `opacity ${duration}ms steps(10)` };
    default:
      return { transition: 'none' };
  }
}

// Icons
const Monitor = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 64, height: 64 }}><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>;
const AlertTriangle = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 64, height: 64 }}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" x2="12" y1="9" y2="13"/><line x1="12" x2="12.01" y1="17" y2="17"/></svg>;
const Loader = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 48, height: 48 }} className="spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>;
const WifiOff = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 64, height: 64 }}><line x1="2" x2="22" y1="2" y2="22"/><path d="M8.5 16.5a5 5 0 0 1 7 0"/><path d="M2 8.82a15 15 0 0 1 4.17-2.65"/><path d="M10.66 5c4.01-.36 8.14.9 11.34 3.76"/><path d="M16.85 11.25a10 10 0 0 1 2.22 1.68"/><path d="M5 13a10 10 0 0 1 5.24-2.76"/><line x1="12" x2="12.01" y1="20" y2="20"/></svg>;

// Styles
const styles = `
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body, #root {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #000;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideInRight {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}

@keyframes slideOutLeft {
  from { transform: translateX(0); }
  to { transform: translateX(-100%); }
}

.player-container {
  width: 100vw;
  height: 100vh;
  background: #000;
  position: relative;
  overflow: hidden;
}

.content-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.content-layer.active {
  z-index: 2;
}

.content-layer.inactive {
  z-index: 1;
}

.content-layer.fade-enter {
  opacity: 0;
}

.content-layer.fade-enter-active {
  opacity: 1;
}

.content-layer.fade-exit {
  opacity: 1;
}

.content-layer.fade-exit-active {
  opacity: 0;
}

.content-layer.slide-enter {
  transform: translateX(100%);
}

.content-layer.slide-enter-active {
  transform: translateX(0);
}

.content-layer.slide-exit {
  transform: translateX(0);
}

.content-layer.slide-exit-active {
  transform: translateX(-100%);
}

.content-image {
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.content-image.cover {
  object-fit: cover;
}

.content-video {
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.content-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.status-screen {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  text-align: center;
  padding: 40px;
}

.status-icon {
  margin-bottom: 24px;
  color: #64748b;
}

.status-icon.warning {
  color: #f59e0b;
}

.status-icon.error {
  color: #ef4444;
}

.status-icon.loading {
  color: #3b82f6;
}

.status-title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 12px;
}

.status-message {
  font-size: 18px;
  color: #94a3b8;
  margin-bottom: 24px;
  max-width: 500px;
}

.status-detail {
  font-size: 14px;
  color: #64748b;
  font-family: monospace;
}

.progress-bar {
  width: 200px;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 24px;
}

.progress-fill {
  height: 100%;
  background: #3b82f6;
  border-radius: 2px;
  transition: width 0.1s linear;
}

.debug-overlay {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  font-family: monospace;
  font-size: 12px;
  z-index: 1000;
  max-width: 300px;
}

.debug-overlay p {
  margin: 4px 0;
}

.reconnect-btn {
  margin-top: 16px;
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
}

.reconnect-btn:hover {
  background: #2563eb;
}
`;

export default function PlayerPage() {
  const [status, setStatus] = useState('initializing'); // initializing, loading, playing, no-content, error, offline
  const [content, setContent] = useState([]);
  const [activeContent, setActiveContent] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayInfo, setDisplayInfo] = useState(null);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [showDebug, setShowDebug] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const timerRef = useRef(null);
  const heartbeatRef = useRef(null);
  const refreshRef = useRef(null);
  const videoRef = useRef(null);
  const iframeRefreshRef = useRef(null);

  // Get stored credentials
  const getCredentials = () => ({
    token: localStorage.getItem('displayToken'),
    displayId: localStorage.getItem('displayId'),
  });

  // Load content from API
  const loadContent = useCallback(async () => {
    const { token, displayId } = getCredentials();
    
    if (!token || !displayId) {
      setStatus('error');
      setError('Display not paired. Please pair this display first.');
      return;
    }

    try {
      setStatus('loading');
      
      // Fetch content for this display
      const data = await apiCall('content.listByDisplay', { displayId: parseInt(displayId) }, token);
      
      if (!data || data.length === 0) {
        setStatus('no-content');
        return;
      }

      // Sort by displayOrder
      const sorted = [...data].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
      setContent(sorted);
      
      // Filter to active content based on schedule
      const active = sorted.filter(item => item.isActive !== false && shouldShowContent(item));
      setActiveContent(active);
      
      if (active.length === 0) {
        setStatus('no-content');
        return;
      }

      setCurrentIndex(0);
      setStatus('playing');
      
    } catch (err) {
      console.error('Failed to load content:', err);
      setStatus('error');
      setError(err.message || 'Failed to load content');
    }
  }, []);

  // Send heartbeat
  const sendHeartbeat = useCallback(async () => {
    const { token, displayId } = getCredentials();
    if (!token || !displayId) return;

    try {
      await apiCall('displays.heartbeat', { displayId: parseInt(displayId) }, token);
    } catch (err) {
      console.error('Heartbeat failed:', err);
    }
  }, []);

  // Initialize
  useEffect(() => {
    const { token, displayId } = getCredentials();
    
    if (!token || !displayId) {
      // Redirect to pairing page
      setTimeout(() => {
        window.location.href = '/pair';
      }, 2000);
      setStatus('error');
      setError('Display not paired. Redirecting to pairing page...');
      return;
    }

    // Load content
    loadContent();

    // Setup heartbeat (every 30 seconds)
    heartbeatRef.current = setInterval(sendHeartbeat, 30000);
    sendHeartbeat(); // Send immediately

    // Setup content refresh (every 5 minutes)
    refreshRef.current = setInterval(loadContent, 5 * 60 * 1000);

    // Debug mode toggle (press 'd' key)
    const handleKeyPress = (e) => {
      if (e.key === 'd' || e.key === 'D') {
        setShowDebug(prev => !prev);
      }
      // Press 'r' to force refresh
      if (e.key === 'r' || e.key === 'R') {
        loadContent();
      }
      // Press 'p' to go to pairing
      if (e.key === 'p' || e.key === 'P') {
        window.location.href = '/pair';
      }
    };
    window.addEventListener('keydown', handleKeyPress);

    return () => {
      clearInterval(heartbeatRef.current);
      clearInterval(refreshRef.current);
      clearInterval(timerRef.current);
      clearInterval(iframeRefreshRef.current);
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [loadContent, sendHeartbeat]);

  // Re-filter active content every minute (for schedule changes)
  useEffect(() => {
    const scheduleCheck = setInterval(() => {
      if (content.length > 0) {
        const active = content.filter(item => item.isActive !== false && shouldShowContent(item));
        setActiveContent(active);
        
        if (active.length === 0) {
          setStatus('no-content');
        } else if (status === 'no-content') {
          setStatus('playing');
          setCurrentIndex(0);
        }
      }
    }, 60000);

    return () => clearInterval(scheduleCheck);
  }, [content, status]);

  // Content advancement timer
  useEffect(() => {
    if (status !== 'playing' || activeContent.length === 0) return;

    const currentItem = activeContent[currentIndex];
    if (!currentItem) return;

    // Clear previous timer
    clearInterval(timerRef.current);
    setProgress(0);

    // For videos, wait for video to end (or use duration)
    if (currentItem.contentType === 'video') {
      // Video handles its own advancement via onEnded
      return;
    }

    // For images and URLs, use duration
    const duration = (currentItem.duration || 10) * 1000;
    const progressInterval = 100;
    let elapsed = 0;

    timerRef.current = setInterval(() => {
      elapsed += progressInterval;
      setProgress((elapsed / duration) * 100);

      if (elapsed >= duration) {
        advanceContent();
      }
    }, progressInterval);

    return () => clearInterval(timerRef.current);
  }, [currentIndex, status, activeContent]);

  // Iframe refresh handling
  useEffect(() => {
    if (status !== 'playing' || activeContent.length === 0) return;

    const currentItem = activeContent[currentIndex];
    if (!currentItem) return;

    clearInterval(iframeRefreshRef.current);

    if ((currentItem.contentType === 'url' || currentItem.contentType === 'domain') && 
        currentItem.iframeRefreshInterval > 0) {
      iframeRefreshRef.current = setInterval(() => {
        const iframe = document.querySelector('.content-iframe');
        if (iframe) {
          iframe.src = iframe.src;
        }
      }, currentItem.iframeRefreshInterval * 1000);
    }

    return () => clearInterval(iframeRefreshRef.current);
  }, [currentIndex, status, activeContent]);

  // Advance to next content
  const advanceContent = useCallback(() => {
    if (activeContent.length <= 1) {
      setCurrentIndex(0);
      return;
    }

    const currentItem = activeContent[currentIndex];
    const transitionDuration = currentItem?.transitionDuration || 500;

    setIsTransitioning(true);
    
    setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % activeContent.length);
      setIsTransitioning(false);
    }, transitionDuration);
  }, [activeContent, currentIndex]);

  // Handle video end
  const handleVideoEnded = useCallback(() => {
    const currentItem = activeContent[currentIndex];
    
    // If looping and single video, restart
    if (currentItem?.videoLoopEnabled && activeContent.length === 1) {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }
      return;
    }

    advanceContent();
  }, [activeContent, currentIndex, advanceContent]);

  // Render content item
  const renderContent = (item, isActive) => {
    if (!item) return null;

    const transformStyle = getTransformStyle(item);
    const transitionStyle = getTransitionCSS(item.transitionEffect, item.transitionDuration);

    const layerClass = `content-layer ${isActive ? 'active' : 'inactive'} ${
      isTransitioning ? (isActive ? `${item.transitionEffect}-enter-active` : `${item.transitionEffect}-exit-active`) : ''
    }`;

    switch (item.contentType) {
      case 'picture':
        return (
          <div className={layerClass} style={transitionStyle}>
            <img 
              src={item.contentUrl} 
              alt="" 
              className="content-image"
              style={{ transform: transformStyle }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        );

      case 'video':
        return (
          <div className={layerClass} style={transitionStyle}>
            <video
              ref={isActive ? videoRef : null}
              src={item.contentUrl}
              className="content-video"
              style={{ transform: transformStyle }}
              autoPlay={isActive}
              muted={item.videoMuted !== false}
              loop={item.videoLoopEnabled && activeContent.length === 1}
              onEnded={handleVideoEnded}
              playsInline
            />
          </div>
        );

      case 'url':
      case 'domain':
        return (
          <div className={layerClass} style={transitionStyle}>
            <iframe
              src={item.contentUrl}
              className="content-iframe"
              style={{ transform: transformStyle }}
              title="Content"
              allow="autoplay; fullscreen"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            />
          </div>
        );

      default:
        // Try to detect type from URL
        if (/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(item.contentUrl)) {
          return (
            <div className={layerClass} style={transitionStyle}>
              <img 
                src={item.contentUrl} 
                alt="" 
                className="content-image"
                style={{ transform: transformStyle }}
              />
            </div>
          );
        }
        if (/\.(mp4|webm|ogg|mov)$/i.test(item.contentUrl)) {
          return (
            <div className={layerClass} style={transitionStyle}>
              <video
                ref={isActive ? videoRef : null}
                src={item.contentUrl}
                className="content-video"
                style={{ transform: transformStyle }}
                autoPlay={isActive}
                muted={item.videoMuted !== false}
                onEnded={handleVideoEnded}
                playsInline
              />
            </div>
          );
        }
        // Default to iframe
        return (
          <div className={layerClass} style={transitionStyle}>
            <iframe
              src={item.contentUrl}
              className="content-iframe"
              style={{ transform: transformStyle }}
              title="Content"
            />
          </div>
        );
    }
  };

  // Render status screens
  if (status === 'initializing' || status === 'loading') {
    return (
      <>
        <style>{styles}</style>
        <div className="status-screen">
          <div className="status-icon loading">
            <Loader />
          </div>
          <h1 className="status-title">Loading Content</h1>
          <p className="status-message">Please wait while we load your display content...</p>
        </div>
      </>
    );
  }

  if (status === 'error') {
    return (
      <>
        <style>{styles}</style>
        <div className="status-screen">
          <div className="status-icon error">
            <AlertTriangle />
          </div>
          <h1 className="status-title">Connection Error</h1>
          <p className="status-message">{error}</p>
          <button className="reconnect-btn" onClick={() => window.location.href = '/pair'}>
            Pair Display
          </button>
          <p className="status-detail">Press 'R' to retry, 'P' for pairing</p>
        </div>
      </>
    );
  }

  if (status === 'no-content') {
    return (
      <>
        <style>{styles}</style>
        <div className="status-screen">
          <div className="status-icon warning">
            <Monitor />
          </div>
          <h1 className="status-title">No Content Available</h1>
          <p className="status-message">
            This display has no active content scheduled for the current time. 
            Please add content in the admin dashboard.
          </p>
          <p className="status-detail">Display ID: {localStorage.getItem('displayId')}</p>
          <p className="status-detail" style={{ marginTop: 8 }}>Auto-refreshing every 5 minutes...</p>
        </div>
      </>
    );
  }

  // Playing content
  const currentItem = activeContent[currentIndex];
  const prevIndex = (currentIndex - 1 + activeContent.length) % activeContent.length;
  const prevItem = activeContent.length > 1 ? activeContent[prevIndex] : null;

  return (
    <>
      <style>{styles}</style>
      <div className="player-container">
        {/* Previous content (for transitions) */}
        {prevItem && isTransitioning && renderContent(prevItem, false)}
        
        {/* Current content */}
        {renderContent(currentItem, true)}

        {/* Debug overlay */}
        {showDebug && (
          <div className="debug-overlay">
            <p><strong>Digital Signs Player</strong></p>
            <p>Display: {localStorage.getItem('displayId')}</p>
            <p>Content: {currentIndex + 1} / {activeContent.length}</p>
            <p>Type: {currentItem?.contentType}</p>
            <p>Duration: {currentItem?.duration}s</p>
            <p>Transition: {currentItem?.transitionEffect}</p>
            <p>Scheduled: {currentItem?.isScheduled ? 'Yes' : 'No'}</p>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <p style={{ marginTop: 8, fontSize: 10 }}>Press 'D' to hide, 'R' to refresh</p>
          </div>
        )}
      </div>
    </>
  );
}
